/**
 * Frontend Cryptography Utilities
 * Handles encryption/decryption of sensitive notes using Web Crypto API
 */

/**
 * Generate a key from a password using PBKDF2
 */
async function deriveKeyFromPassword(password, salt) {
  const encoder = new TextEncoder()
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  )

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * Encrypt text using AES-GCM with password-derived key
 */
export async function encryptText(plaintext, password) {
  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(plaintext)
    
    // Generate random salt and IV
    const salt = window.crypto.getRandomValues(new Uint8Array(16))
    const iv = window.crypto.getRandomValues(new Uint8Array(12))
    
    // Derive key from password
    const key = await deriveKeyFromPassword(password, salt)
    
    // Encrypt the data
    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      data
    )
    
    // Combine salt, iv, and encrypted data
    const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength)
    combined.set(salt, 0)
    combined.set(iv, salt.length)
    combined.set(new Uint8Array(encrypted), salt.length + iv.length)
    
    // Convert to base64 for storage
    return btoa(String.fromCharCode(...combined))
    
  } catch (error) {
    console.error('❌ Crypto: Encryption failed:', error)
    throw new Error('Encryption failed: ' + error.message)
  }
}

/**
 * Decrypt text using AES-GCM with password-derived key
 */
export async function decryptText(encryptedBase64, password) {
  try {
    // Convert from base64
    const combined = new Uint8Array(
      atob(encryptedBase64).split('').map(c => c.charCodeAt(0))
    )
    
    // Extract salt, iv, and encrypted data
    const salt = combined.slice(0, 16)
    const iv = combined.slice(16, 28)
    const encrypted = combined.slice(28)
    
    // Derive key from password
    const key = await deriveKeyFromPassword(password, salt)
    
    // Decrypt the data
    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encrypted
    )
    
    // Convert back to text
    const decoder = new TextDecoder()
    const decryptedText = decoder.decode(decrypted)
    
    // Check if the decrypted text looks like garbage (likely wrong password)
    const looksLikeGarbage = decryptedText.length > 0 && (
      /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/.test(decryptedText) || // Contains control characters
      decryptedText.split('').some(char => char.charCodeAt(0) > 127) || // Contains non-ASCII characters
      decryptedText.length > 0 && !/[a-zA-Z0-9\s.,!?-_]/.test(decryptedText.substring(0, Math.min(10, decryptedText.length))) // First 10 chars don't look like normal text
    )
    
    if (looksLikeGarbage) {
      console.warn('⚠️ Crypto: Decrypted text looks like garbage, likely wrong password:', decryptedText.substring(0, 50))
      throw new Error('Decryption produced unreadable text. The password may be incorrect.')
    }
    
    return decryptedText
    
  } catch (error) {
    console.error('❌ Crypto: Decryption failed:', error)
    throw new Error('Decryption failed: ' + error.message)
  }
}

/**
 * Encrypt a note object for sensitive storage
 */
export async function encryptNote(note, password) {
  try {
    console.log('🔐 Crypto: Encrypting note:', note.title)
    
    // Fields to encrypt
    const sensitiveFields = ['title', 'content', 'tags']
    const encryptedNote = { ...note }
    
    // Encrypt each sensitive field
    for (const field of sensitiveFields) {
      if (note[field] !== null && note[field] !== undefined) {
        const value = typeof note[field] === 'string' ? note[field] : JSON.stringify(note[field])
        encryptedNote[field] = await encryptText(value, password)
      }
    }
    
    // Mark as encrypted
    encryptedNote.isEncrypted = true
    encryptedNote.encryptedAt = new Date().toISOString()
    
    console.log('✅ Crypto: Note encrypted successfully')
    return encryptedNote
    
  } catch (error) {
    console.error('❌ Crypto: Note encryption failed:', error)
    throw error
  }
}

/**
 * Decrypt a note object from sensitive storage
 */
export async function decryptNote(encryptedNote, password) {
  try {
    console.log('🔓 Crypto: Decrypting note:', encryptedNote.noteId)
    console.log('🔍 Crypto: Input note structure:', {
      noteId: encryptedNote.noteId,
      title: encryptedNote.title,
      content: encryptedNote.content?.substring(0, 50),
      isEncrypted: encryptedNote.isEncrypted,
      encryptedAt: encryptedNote.encryptedAt
    })
    
    if (!encryptedNote.isEncrypted) {
      console.warn('⚠️ Crypto: Note is not marked as encrypted, returning as-is')
      return encryptedNote
    }
    
    // Fields to decrypt
    const sensitiveFields = ['title', 'content', 'tags']
    const decryptedNote = { ...encryptedNote }
    
    // Decrypt each sensitive field
    for (const field of sensitiveFields) {
      if (encryptedNote[field] !== null && encryptedNote[field] !== undefined) {
        try {
          console.log(`🔍 Crypto: Decrypting field ${field}:`, encryptedNote[field]?.substring(0, 50))
          const decryptedValue = await decryptText(encryptedNote[field], password)
          console.log(`🔍 Crypto: Decrypted field ${field}:`, decryptedValue?.substring(0, 50))
          
          // Try to parse as JSON for arrays/objects, otherwise keep as string
          if (field === 'tags') {
            try {
              decryptedNote[field] = JSON.parse(decryptedValue)
            } catch {
              decryptedNote[field] = decryptedValue
            }
          } else {
            decryptedNote[field] = decryptedValue
          }
        } catch (error) {
          console.error(`❌ Crypto: Failed to decrypt field ${field}:`, error)
          throw new Error(`Failed to decrypt note field: ${field}`)
        }
      }
    }
    
    // Remove encryption metadata for clean note object
    delete decryptedNote.isEncrypted
    delete decryptedNote.encryptedAt
    
    console.log('✅ Crypto: Note decrypted successfully')
    console.log('🔍 Crypto: Decrypted content preview:', decryptedNote.content?.substring(0, 100))
    console.log('🔍 Crypto: Full decrypted note object:', decryptedNote)
    console.log('🔍 Crypto: Content property exists:', 'content' in decryptedNote)
    console.log('🔍 Crypto: Content value:', decryptedNote.content)
    return decryptedNote
    
  } catch (error) {
    console.error('❌ Crypto: Note decryption failed:', error)
    throw error
  }
}

/**
 * Test if Web Crypto API is available
 */
export function isCryptoSupported() {
  return (
    typeof window !== 'undefined' &&
    window.crypto &&
    window.crypto.subtle &&
    typeof window.crypto.subtle.encrypt === 'function'
  )
}

/**
 * Generate a secure random password for testing
 */
export function generateSecurePassword(length = 16) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
  const array = new Uint8Array(length)
  window.crypto.getRandomValues(array)
  
  return Array.from(array, byte => charset[byte % charset.length]).join('')
}
